(ns pfo.staging.controller
  (:require
    [pfo.api :as api :refer [process-error]]
    [pfo.flash :refer [set-flash]]
    [promesa.core :as p]
    [pfo.util :as util]
    [keechma.next.controller :as ctrl]))

;; Staff document review queue (#2851). Backed by the real endpoints under
;; /api/v1/document/staging/queue/:
;;
;;   GET  /document/staging/queue/                  list pending submissions
;;   POST /document/staging/queue/{id}/claim/       submitted -> under_review
;;   POST /document/staging/queue/{id}/reject/      -> rejected (requires reason)
;;   POST /document/staging/queue/{id}/file/        -> filed   (requires collection_ids)
;;   POST /document/staging/queue/conversation/     claims document_ids, opens AARK
;;
;; {id} is the document_id. Each list row carries the AARK first-pass
;; classification (#2854 Stage 3) as :classification_status +
;; :classification_proposal, read through pfo.staging.aark.
;;
;; Action handlers do not mutate state* directly; callers pass :on-success to
;; reload the queue so the row's status/visibility reflects the server.

(derive :staging-queue :keechma/controller)

(defmethod ctrl/handle :staging-queue
  [{:keys [state*] :as ctrl} event
   {:keys [document-id document-ids reason comments family-id collection-ids main-collection-id
           category-id name clear-name ext-ref year month day quarter win
           on-success on-error errors-atom]}]
  (case event
    :load
    (->> (api/ajaxp "/document/staging/queue/"
                    {:method :get
                     :params (util/remove-nil-values {:family_id family-id})})
         (p/map (fn [response]
                  (ctrl/transact ctrl #(reset! state* response))
                  (some-> on-success (apply [response]))))
         (p/error (or on-error (fn [error-response] (throw error-response))))
         (process-error errors-atom))

    :claim
    (->> (api/ajaxp (str "/document/staging/queue/" document-id "/claim/")
                    {:method :post})
         (p/map (fn [response]
                  (some-> on-success (apply [response]))))
         (p/error (or on-error (fn [error-response] (throw error-response))))
         (process-error errors-atom))

    :reject
    (->> (api/ajaxp (str "/document/staging/queue/" document-id "/reject/")
                    {:method :post
                     :body (util/make-params (util/remove-nil-values {:reason   reason
                                                                      :comments comments}))})
         (p/map (fn [response]
                  (some-> on-success (apply [response]))))
         (p/error (or on-error (fn [error-response] (throw error-response))))
         (process-error errors-atom))

    :file
    (->> (api/ajaxp (str "/document/staging/queue/" document-id "/file/")
                    {:method :post
                     :body (util/make-params
                             (util/remove-nil-values
                               {:collection_ids     collection-ids
                                :main_collection_id main-collection-id
                                :category_id        category-id
                                :name               name
                                :clear_name         (when clear-name true)
                                :ext_ref            ext-ref
                                :year               year
                                :month              month
                                :day                day
                                :quarter            quarter}))})
         (p/map (fn [response]
                  (some-> on-success (apply [response]))))
         (p/error (or on-error (fn [error-response] (throw error-response))))
         (process-error errors-atom))

    ;; Claims the selected documents and opens the reviewer's AARK conversation
    ;; about them on the AARK page (#2928).
    :file-with-aark
    (->> (api/ajaxp "/document/staging/queue/conversation/"
                    {:method :post
                     :body (util/make-params {:document_ids document-ids})})
         (p/map (fn [response]
                  (util/set-location (str "#/chat?conversation_id=" (get-in response [:conversation :id])))))
         ;; The 403/409/400 bodies say why (claimed, AARK still reading, two
         ;; families); the shared handler would show a generic text instead.
         (p/error (fn [error-response]
                    (some-> on-error (apply [error-response]))
                    (if-let [message (get-in error-response [:response :body :error])]
                      (set-flash :error message)
                      (api/handle-error-response errors-atom error-response)))))

    ;; Open the staged document in a viewer tab. A blank tab is opened
    ;; synchronously by the caller (to dodge popup blockers) and passed as :win;
    ;; we point it at the short-lived session-less preview URL once it arrives.
    :preview
    (->> (api/ajaxp (str "/document/staging/queue/" document-id "/preview-url/")
                    {:method :get})
         (p/map (fn [response]
                  (if-let [u (:url response)]
                    (if win (set! (.-location win) u) (js/window.open u "_blank"))
                    (when win (.close win)))))
         (p/error (fn [error-response]
                    (when win (.close win))
                    (or (and on-error (on-error error-response))
                        (throw error-response))))
         (process-error errors-atom))

    ;; Resolve the short-lived session-less preview URL and hand it back via
    ;; :on-success (no window navigation). The 3-pane review workspace uses this
    ;; to embed the document inline: the URL is same-origin and streams the
    ;; bytes, so it can be fed straight to pdfjs (`pdf-view` :load-pdf) for PDFs
    ;; or used as an <img>/fallback src for other types.
    :preview-url
    (->> (api/ajaxp (str "/document/staging/queue/" document-id "/preview-url/")
                    {:method :get})
         (p/map (fn [response]
                  (some-> on-success (apply [response]))))
         (p/error (or on-error (fn [error-response] (throw error-response))))
         (process-error errors-atom))

    :clear (reset! state* nil)
    nil))

;; The caller's own uploads with their review status ("My uploads"), from
;; GET /api/v1/document/staging/.
(derive :my-uploads :keechma/controller)

(defonce ^:private my-uploads-load (atom 0))

(defmethod ctrl/handle :my-uploads
  [{:keys [state*] :as ctrl} event _]
  (case event
    ;; The app instance outlives a logout and the tab remounts often, so a
    ;; load starts empty and only the newest load for the same user may land.
    :load
    (let [user-id (api/get-auth-info :user-id)
          load-id (swap! my-uploads-load inc)
          current? #(and (= load-id @my-uploads-load) (= user-id (api/get-auth-info :user-id)))]
      (reset! state* nil)
      (->> (api/ajaxp "/document/staging/" {:method :get})
           (p/map (fn [response]
                    (when (current?)
                      (ctrl/transact ctrl #(reset! state* response)))))
           (p/error (fn [error-response]
                      (when (current?)
                        (ctrl/transact ctrl #(reset! state* :error))
                        (throw error-response))))
           (process-error nil)))
    nil))
